import React, { useContext, useEffect, useMemo, useState } from "react"
import { Box, Divider, Typography, Modal, Button, CircularProgress } from "@mui/material"
import { JobTile } from "../JobTile/JobTile"
import { EJobStatus, IJob, IJobListing, IStop } from "../../entities/job/interface"
import ShiftContext from "../../contexts/ShiftContext"
import { useJobContext } from "../../contexts/JobContext"
import {
    useUpdateStatusHook,
    jobInDeliveryPhase,
    jobInPickupPhase,
    getNextJobStatus,
    clearSessionStorage,
    useBulkUpdateStatusHook,
    revertJobsQuantity,
} from "../../entities/job/helper"
import { useWindowDimensions } from "../../utils/styling"
import { updatedRoadMapStops } from "../../utils/job"
import { CenterCircular } from "../../components/Loading"
import ConfirmationDialog from "../ConfirmationDialog"
import { hiddenScrollbar } from "../../styles/theme"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

const swipeThreshold = 60 // percentage of the screen width to swipe before action is triggered

const RoadMap: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    //translation
    const { t } = useTranslation()
    const shiftContext = useContext(ShiftContext)
    const { handleJobUpdate, jobUpdateMutationLoading } = useUpdateStatusHook()
    const { handleBulkJobsUpdate, loading } = useBulkUpdateStatusHook()
    const {
        currentDestinationIndex,
        groupId,
        startNewJobPart,
        currentJobs,
        setCurrentJobStatus,
        setIsRoadMap,
        isRoadMap,
        setCurrentJobs,
    } = useJobContext()
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [resetJobsDialog, setResetJobsDialog] = useState(false)
    const [title, setTitle] = useState("")
    const [message, setMessage] = useState("")
    const [isSelect, setIsSelect] = useState(false)
    const [jobIds, setJobIds] = useState<string[]>([])
    // const [revertedJobs, setRevertedJobs] = useState<IJob[]>([])
    const [isLoading, setIsLoading] = useState(false)

    const navigate = useNavigate()

    //swipe threshold
    const { width } = useWindowDimensions()
    const thresholdX = useMemo(() => (width * swipeThreshold) / 100, [width])

    const stops = (groupId && shiftContext?.stops?.[groupId]?.destinationStops) || []
    const jobs: Record<string, IJob> | null | undefined = shiftContext?.jobs

    const flatStops = stops.flat()

    // Check if all jobs are idle and all stops are idle then navigate to job-listing
    useEffect(() => {
        if (!flatStops || flatStops.length === 0) return // Prevent errors if flatStops is undefined or empty

        // Check if all jobs are idle
        // Check if all stops are idle
        const allJobsIdle = jobs ? Object.values(jobs).every((job) => job.jobStatus === "idle") : false
        const allStopsIdle = flatStops.every((job) => job.status === "idle")

        const result = window.navigator.onLine ? allStopsIdle : allJobsIdle

        if (isRoadMap && result) {
            setIsRoadMap(false)
            clearSessionStorage([
                "destinationStops",
                "currentDestinationIndex",
                "currentStopIndex",
                "groupId",
                "completedJobGroups",
                "screens",
            ])
            // shiftContext?.updateJob([])
            navigate("/job-listing")
        }
    }, [flatStops, jobIds])

    if (!shiftContext) return

    // Get all jobs from stops and assign numbers to them

    const allJobs = flatStops
        .map((stop) => {
            const job = shiftContext.jobs?.[stop.jobId]
            return job ? { ...job, type: stop.type } : null
        })
        .filter(Boolean) as IJob[]

    const assignNumbers = (jobs: IJob[]) => {
        const jobNumberMap = new Map()
        let counter = 1

        return jobs.map((job: IJob) => {
            if (!jobNumberMap.has(job._id)) {
                jobNumberMap.set(job._id, counter)
                counter++
            }
            return {
                ...job,
                number: job.type === "pickup" ? `P${jobNumberMap.get(job._id)}` : `D${jobNumberMap.get(job._id)}`,
            }
        })
    }

    // Filter out null jobs and assign numbers to each job based on its type (pickup or delivery)
    const allNumberedJobs = assignNumbers(allJobs)

    // Sort pickupJobs and deliveryJobs based on their assigned number
    const pickupJobs = allNumberedJobs
        .filter((job) => job.type === "pickup")
        .sort((a, b) => Number(a.number.substring(1)) - Number(b.number.substring(1)))

    // Sort pickupJobs and deliveryJobs based on their assigned number
    const deliveryJobs = allNumberedJobs
        .filter((job) => job.type === "delivery")
        .sort((a, b) => Number(a.number.substring(1)) - Number(b.number.substring(1)))

    const updatedJobs = [...pickupJobs, ...deliveryJobs]

    // Filter out jobs that are not in shiftContext.jobs
    const selectedJobs = jobIds
        .map((id) => shiftContext.jobs && shiftContext.jobs[id])
        .filter((job) => job !== undefined) // filters out any unmatched IDs

    const handleJobStarted = async (job: IJob) => {
        // Check if the job is already completed (loaded, unloaded or completed)
        const isPickup = job.type === "pickup" && jobInPickupPhase(job.jobStatus) && job.jobStatus !== "loaded"
        const isDelivery =
            job.type === "delivery" &&
            job.jobStatus !== "completed" &&
            (job.jobStatus === "loaded" || jobInDeliveryPhase(job.jobStatus))

        // we need to show modal if the job is already completed
        // user can not start delivery without completing pickup
        if (!shiftContext?.stops || (!isPickup && !isDelivery)) {
            let title = ""
            let message = ""

            if (job.jobStatus === "completed") {
                title = "Already Completed"
                message = "This job is already completed, you need to start another one!"
            } else if (job.type === "pickup" && !isPickup) {
                title = "Already Picked Up"
                message = "This pickup part is already completed, you need to start another one!"
            } else if (job.type === "delivery" && !isDelivery) {
                title = "Pickup Not Completed"
                message = "You need to complete the pickup part first!"
            }

            if (title && message) {
                setTitle(title)
                setMessage(message)
                setShowConfirmDialog(true)
            }
            return
        }

        // If only one job exists and it's the same as the current job, just close the modal and return
        if (currentJobs?.length === 1 && currentJobs[0]._id === job._id) {
            if ([EJobStatus.idle, EJobStatus.loaded].includes(currentJobs[0].jobStatus)) {
                const jobStatus =
                    currentJobs[0].jobStatus === EJobStatus.idle
                        ? EJobStatus.onRouteToPickup
                        : EJobStatus.onRouteToDelivery
                await handleBulkJobsUpdate([job._id], jobStatus)
                startNewJobPart(job._id, job.type)
            }
            setIsRoadMap(false)
            window.sessionStorage.setItem("screens", JSON.stringify({ consecutive: "false", roadMap: "false" }))
            onClose()
            return
        }

        // filter the jobs that are not completed means the jobs that are not in idle, loaded(pickup), unloaded(delivery) and completed status
        // then update the status of all the jobs
        // when user navigate back then we update status to idle if the job is onRouteToPickup or loaded if the job is onRouteToDelivery
        const jobIds = currentJobs
            ?.filter(
                (currentJob) =>
                    currentJob._id !== job._id &&
                    ![EJobStatus.completed, EJobStatus.loaded, EJobStatus.unloaded, EJobStatus.idle].includes(
                        currentJob.jobStatus
                    )
            )
            .filter((job) => !job.isJobCompleted)
            .map((job) => job._id)

        const jobStatus =
            currentJobs && currentJobs[0]?.jobStatus === EJobStatus.onRouteToPickup
                ? EJobStatus.idle
                : EJobStatus.loaded
        // Update all jobs to the next status
        if (jobIds?.length) await handleBulkJobsUpdate(jobIds, jobStatus)

        // Set the selected job in the context
        setCurrentJobStatus(jobStatus)

        const stops = updatedRoadMapStops(job, shiftContext.stops, currentDestinationIndex, groupId)
        if (!stops) return

        await handleBulkJobsUpdate([job._id], getNextJobStatus(job.jobStatus))
        startNewJobPart(job._id, job.type)

        shiftContext.setStops(stops)
        window.sessionStorage.setItem("destinationStops", JSON.stringify(stops[groupId].destinationStops))
        setIsRoadMap(false)
        window.sessionStorage.setItem("screens", JSON.stringify({ consecutive: "false", roadMap: "false" }))
        onClose()
    }

    // we need to reset the jobs to idle which are selected and update stops and context accordingly
    const handleResetJobs = async () => {
        let currentJobs: IJob[] = []

        setIsLoading(true)
        await handleBulkJobsUpdate(jobIds, EJobStatus.idle)

        // Filter jobs based on jobIds and set them as current jobs
        jobIds.forEach(async (jobId) => {
            const job = jobs && Object.values(jobs).filter((job) => job._id === jobId)
            if (job) {
                currentJobs = [...currentJobs, ...job]
            }
        })

        // if we have consecutive jobs but user reset one job so we need to update the stops accordingly
        // or if he select all consective jobs then we return the same stop
        // also we need to update the current jobs in the context and session storage
        const filterStops = (jobIds: string[], type: string): IStop[][] => {
            let updatedStops: IStop[][] = []
            stops.forEach((stop, index) => {
                const matchingJobs = stop.filter((item) => item.type === type && jobIds.includes(item.jobId))
                const nonMatchingJobs = stop.filter((item) => !matchingJobs.includes(item))

                if (matchingJobs.length === stop.length || !matchingJobs.length) {
                    // If all items in this stop match the jobIds, keep the whole subarray
                    updatedStops.push(stop)
                } else if (matchingJobs.length > 0) {
                    // If only some items match, keep them in place and add the split subarray right after
                    updatedStops.push(nonMatchingJobs)
                    updatedStops.push(matchingJobs)
                }
            })

            return updatedStops
        }

        const result = filterStops(jobIds, "pickup")

        if (currentJobs.length === 0) return
        setCurrentJobs(currentJobs)

        //update the stops in format like first all completed stops and then all not completed stops
        const updatedStops: IStop[][] = []
        const completedStops: IStop[][] = []

        // push all stops that are completed
        result.forEach((stop) => {
            if (stop.every((job) => job.status === "completed")) {
                updatedStops.push(stop)
                completedStops.push(stop)
            }
        })

        // push all stops that are not completed
        result.forEach((stop) => {
            if (!stop.every((job) => job.status === "completed")) {
                updatedStops.push(stop)
            }
        })

        // Current index is updated just after the completed stops
        // so we need to add 1 to the length of completed stops

        const currentIndex = completedStops.length + 1
        window.sessionStorage.setItem("destinationStops", JSON.stringify(updatedStops))
        window.sessionStorage.setItem("currentDestinationIndex", JSON.stringify(currentIndex))

        // this unloads means we are updating the status to idle
        jobIds.forEach(async (jobId) => {
            startNewJobPart(jobId, "unloaded")
        })

        // update the status of the selected jobs to idle
        await handleBulkJobsUpdate(jobIds, EJobStatus.idle)

        // after resetting we need to empty the jobIds and set the isSelect to false
        await revertJobsQuantity(EJobStatus.atPickupLocation, selectedJobs, handleJobUpdate)
        setIsSelect(false)
        setIsLoading(false)
        setJobIds([])
    }

    const handleConfirm = async () => {
        setShowConfirmDialog(false)
        if (resetJobsDialog) {
            await handleResetJobs()
            setResetJobsDialog(false)
        }
    }

    const handleDialogClose = () => {
        setShowConfirmDialog(false)
        setResetJobsDialog(false)
    }

    if (loading || isLoading) {
        return <CenterCircular />
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="job-details-modal"
            aria-describedby="job-details-description"
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflowX: "hidden",
                overflow: "hidden",
            }}
        >
            <Box
                width={"90%"}
                maxWidth={700}
                display="flex"
                flexDirection="column"
                bgcolor={"secondary.light"}
                position={"absolute"}
                borderRadius={6}
                alignItems={"center"}
                justifyContent={"space-between"}
                mt={"10%"}
                paddingY={5}
                height={"80vh"}
                sx={hiddenScrollbar}
            >
                <Box
                    display="flex"
                    alignItems={"center"}
                    flexDirection="column"
                    borderRadius={6}
                    width={"100%"}
                    overflow={"auto"}
                >
                    <Typography variant="h4" color="white" fontWeight={"bold"} sx={{ marginBottom: 3 }}>
                        Road Map
                    </Typography>
                    <Divider
                        sx={{
                            borderColor: "white",
                            opacity: 0.5,
                            borderWidth: 1,
                            borderStyle: "dashed",
                            width: "90%",
                            marginBottom: 1,
                        }}
                    />
                    {isRoadMap && (
                        <Box width={"90%"} display={"flex"} justifyContent={"space-between"}>
                            <Button
                                onClick={() => {
                                    setIsSelect(!isSelect)
                                    setJobIds([])
                                }}
                                sx={{ color: "white" }}
                            >
                                {isSelect ? "cancel" : "select"}
                            </Button>
                            <Button
                                onClick={() => {
                                    setResetJobsDialog(true)
                                    setTitle(t("jobs.mobile.backAction.unloadingJob.title"))
                                    setMessage(t("jobs.mobile.backAction.unloadingJob.subTitle"))
                                }}
                                sx={{ color: "white" }}
                                disabled={jobIds.length === 0}
                            >
                                {jobUpdateMutationLoading ? (
                                    <CircularProgress size={20} sx={{ color: "white" }} />
                                ) : (
                                    "reset"
                                )}
                            </Button>
                        </Box>
                    )}
                    <Box
                        paddingX={1}
                        width={"100%"}
                        display="flex"
                        alignItems={"center"}
                        flexDirection="column"
                        sx={{ ...hiddenScrollbar, overflowX: "hidden" }}
                    >
                        <Box width={"90%"} mt={3}>
                            {updatedJobs?.map((job: IJob, index: number) => {
                                const originOnly = job.type === "pickup"
                                const destinationOnly = job.type === "delivery"
                                const commonProps = {
                                    handleJobStarted,
                                    swipeThreshold: thresholdX,
                                }
                                const isPickup =
                                    job.type === "pickup" &&
                                    (!jobInPickupPhase(job.jobStatus) || job.jobStatus === "loaded")

                                const isDelivery = job.type === "delivery" && job.jobStatus === "completed"

                                return (
                                    <JobTile
                                        key={index}
                                        job={job as IJobListing}
                                        index={""}
                                        avatarColor={isPickup || isDelivery ? "#388e3c" : "white"}
                                        content={job.number}
                                        isCheckAvatar={true}
                                        isSwipe={true}
                                        originOnly={originOnly}
                                        destinationOnly={destinationOnly}
                                        {...commonProps}
                                        select={isSelect && job.jobStatus !== "completed" && isPickup}
                                        onCheck={() => {
                                            if (jobIds.includes(job._id)) {
                                                setJobIds(jobIds.filter((id) => id !== job._id))
                                            } else {
                                                setJobIds([...jobIds, job._id])
                                            }
                                        }}
                                        selectColor={jobIds.includes(job._id) ? "#388e3c" : "white"}
                                    />
                                )
                            })}
                        </Box>
                        {!isRoadMap && (
                            <Box width={"100%"} display={"flex"} justifyContent={"center"}>
                                <Button
                                    onClick={() => {
                                        onClose()
                                    }}
                                    variant={"outlined"}
                                    sx={{
                                        backgroundColor: "transparent",
                                        color: "white",
                                        width: "60%",
                                        marginTop: 3,
                                        borderColor: "white",
                                        paddingY: "14px",
                                        borderWidth: 2,
                                        "&:hover": {
                                            borderColor: "white",
                                            borderWidth: 2,
                                        },
                                        borderRadius: 3,
                                    }}
                                >
                                    Close
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
                <ConfirmationDialog
                    open={showConfirmDialog || resetJobsDialog}
                    title={title}
                    content={message}
                    actions={{
                        confirmText: resetJobsDialog ? t("common.yes") : t("common.ok"),
                        cancelText: resetJobsDialog ? t("common.no") : "",
                        onConfirm: handleConfirm,
                        onCancel: handleDialogClose,
                    }}
                    onClose={onClose}
                />
            </Box>
        </Modal>
    )
}

export default RoadMap
